import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { fr as frLocale } from "date-fns/locale";
import { ChevronLeft, ChevronRight, X, Users, BedDouble, MapPin, Waves, Share2, Mail, MessageSquare, MessageCircle, Send, Facebook, Instagram, Play, Ruler, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useLang } from "@/i18n";
import { useBooking } from "@/BookingContext";
import Reveal from "@/components/Reveal";

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}

export default function PropertyDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { openBooking } = useBooking();
  const isMobile = useIsMobile();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!property) return;
    document.title = property.seo_title || `${property.name} — Présence Home Services`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute("content", property.seo_description || `${property.name} — location de vacances${property.location ? ` à ${property.location}` : ""}, Corse-du-Sud — Présence Home Services.`);
    }
  }, [property]);
  const [shareOpen, setShareOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const [reviews, setReviews] = useState([]);
  const descRef = useRef(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const [stayRange, setStayRange] = useState();
  const [estGuests, setEstGuests] = useState(2);
  const [estimate, setEstimate] = useState(null);

  const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    if (!stayRange?.from || !stayRange?.to || !property) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    api.post(`/properties/${slug}/estimate`, {
      arrival: isoDay(stayRange.from),
      departure: isoDay(stayRange.to),
      guests: estGuests,
    })
      .then((r) => { if (!cancelled) setEstimate(r.data); })
      .catch(() => { if (!cancelled) setEstimate(null); });
    return () => { cancelled = true; };
  }, [stayRange, estGuests, property, slug]);

  useEffect(() => {
    setProperty(null);
    setNotFound(false);
    api.get(`/properties/${slug}`).then((r) => setProperty(r.data)).catch(() => setNotFound(true));
    api.get(`/properties/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, [slug]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  useEffect(() => {
    setExpanded(false);
    const el = descRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => setClamped(el.scrollHeight > el.clientHeight + 2));
    return () => cancelAnimationFrame(raf);
  }, [property, lang]);

  if (notFound) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="property-not-found">
        <div className="text-center">
          <p className="font-serif text-3xl text-maquis-deep">404</p>
          <Link to="/locations" className="btn-outline-elegant mt-8 text-maquis-deep hover:bg-maquis-deep hover:text-casse">
            {t.property.back}
          </Link>
        </div>
      </main>
    );
  }

  if (!property) {
    return <main className="flex min-h-screen items-center justify-center pt-20 text-maquis-soft">{t.auth.checking}</main>;
  }

  const desc = lang === "en" ? property.description.en || property.description.fr : property.description.fr;
  const short = lang === "en" ? property.short_description?.en || property.short_description?.fr : property.short_description?.fr;
  const blocked = (property.blocked_dates || []).map((d) => new Date(d + "T12:00:00"));

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const rawVideoUrl = property.video_url || "";
  const videoFile = property.video_file || (rawVideoUrl.startsWith("/api/files/") ? rawVideoUrl : "");
  const externalVideoUrl = rawVideoUrl.startsWith("/api/files/") ? "" : rawVideoUrl;
  const shareItems = [
    { key: "mail", label: "Mail", icon: Mail, href: `mailto:?subject=${encodeURIComponent(property.name + " — Présence Home Services")}&body=${encodeURIComponent(shareUrl)}` },
    { key: "message", label: "Message", icon: MessageSquare, href: `sms:?&body=${encodeURIComponent(shareUrl)}` },
    { key: "whatsapp", label: "WhatsApp", icon: Send, href: `https://wa.me/?text=${encodeURIComponent(shareUrl)}` },
    { key: "messenger", label: "Messenger", icon: MessageCircle, href: `fb-messenger://share?link=${encodeURIComponent(shareUrl)}` },
    { key: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
  ];
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t.property.copied);
    } catch (e) { /* noop */ }
    setShareOpen(false);
  };

  const info = [
    { icon: Users, label: t.property.guests, value: property.guests, testid: "property-guests" },
    { icon: BedDouble, label: t.property.bedrooms, value: property.bedrooms, testid: "property-bedrooms" },
    { icon: Ruler, label: t.gallery.surface, value: property.surface ? `${property.surface} m²` : "—", testid: "property-surface" },
    { icon: MapPin, label: t.property.location, value: property.location, testid: "property-location" },
    { icon: Waves, label: t.property.pool, value: property.pool ? t.property.pool_yes : t.property.pool_no, testid: "property-pool" },
  ];

  return (
    <main data-testid="property-detail-page">
      <header className="relative flex h-[75vh] min-h-[480px] items-end overflow-hidden">
        <img src={property.cover_image || property.images[0]} alt={property.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-mer/80 via-mer/25 to-mer/35" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 text-casse">
          <Link to="/locations" data-testid="property-back-link" className="text-xs uppercase tracking-[0.22em] text-casse/70 hover:text-casse transition-colors">
            ← {t.property.back}
          </Link>
          <p className="eyebrow mt-6 text-sable">{property.location}</p>
          <h1 data-testid="property-name" className="mt-3 font-serif italic text-5xl sm:text-6xl lg:text-7xl">{property.name}</h1>
        </div>
      </header>

      <section className="border-b border-maquis-deep/10 bg-ivoire">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 py-10 sm:px-8 sm:grid-cols-3 lg:grid-cols-5">
          {info.map(({ icon: Icon, label, value, testid }) => (
            <div key={label} data-testid={testid} className="flex items-start gap-4">
              <Icon size={26} strokeWidth={1} className="mt-1 text-maquis-olive" />
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-maquis-soft">{label}</p>
                <p className="mt-1 font-serif text-xl text-maquis-deep">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Présentation + partage */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 sm:pt-20" data-testid="property-intro-section">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-4xl">
            <p data-testid="property-intro" className="text-left font-serif italic text-base sm:text-lg leading-relaxed text-maquis-olive">
              {short}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-8">
              <div className="relative">
                <button
                  data-testid="share-button"
                  onClick={() => setShareOpen((o) => !o)}
                  className="inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-1 text-xs uppercase tracking-[0.2em] text-maquis-deep transition-colors duration-300 hover:border-maquis-deep"
                >
                  <Share2 size={15} strokeWidth={1.25} /> {t.property.share}
                </button>
                {shareOpen && (
                  <div data-testid="share-menu" className="absolute left-0 top-full z-30 mt-3 w-56 rounded-md border border-maquis-deep/10 bg-casse p-2 shadow-[0_24px_60px_-24px_rgba(42,51,35,0.4)]">
                    {shareItems.map(({ key, label, icon: Icon, href }) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`share-${key}`}
                        className="flex items-center gap-3 rounded-sm px-4 py-2.5 text-sm text-maquis-deep transition-colors hover:bg-ivoire"
                      >
                        <Icon size={16} strokeWidth={1.25} className="text-maquis-olive" /> {label}
                      </a>
                    ))}
                    <button
                      data-testid="share-instagram"
                      onClick={copyLink}
                      className="flex w-full items-center gap-3 rounded-sm px-4 py-2.5 text-left text-sm text-maquis-deep transition-colors hover:bg-ivoire"
                    >
                      <Instagram size={16} strokeWidth={1.25} className="text-maquis-olive" /> Instagram
                    </button>
                  </div>
                )}
              </div>
              {externalVideoUrl && (
                <a
                  data-testid="property-video-button"
                  href={externalVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-1 text-xs uppercase tracking-[0.2em] text-maquis-deep transition-colors duration-300 hover:border-maquis-deep"
                >
                  <Play size={15} strokeWidth={1.25} /> {t.property.watch_video}
                </a>
              )}
            </div>
          </div>
          <button
            data-testid="property-reserve-button-top"
            onClick={() => openBooking({ propertyId: property.id })}
            className="btn-outline-elegant w-fit shrink-0 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
          >
            {t.nav.reserve}
          </button>
        </div>
      </section>

      {/* Galerie */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 sm:pb-28 pt-4" data-testid="property-gallery-section">
        <div className="relative">
          <div className="overflow-hidden rounded-md" ref={emblaRef} data-testid="property-gallery">
            <div className="flex">
              {property.images.map((src, i) => (
                <button
                  key={i}
                  className="relative min-w-0 flex-[0_0_100%] sm:flex-[0_0_70%] mr-4"
                  onClick={() => setLightbox(i)}
                  data-testid={`gallery-slide-${i}`}
                >
                  <img src={src} alt={`${property.name} ${i + 1}`} loading="lazy"
                    className="h-72 w-full rounded-md object-cover sm:h-[480px]" />
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="gallery-prev-button"
            onClick={() => emblaApi && emblaApi.scrollPrev()}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-casse/90 p-3 text-maquis-deep shadow-md transition-colors hover:bg-casse"
            aria-label="Précédent"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            data-testid="gallery-next-button"
            onClick={() => emblaApi && emblaApi.scrollNext()}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-casse/90 p-3 text-maquis-deep shadow-md transition-colors hover:bg-casse"
            aria-label="Suivant"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </div>
        <div className="mt-4 flex justify-center gap-2">
          {property.images.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === selected ? "w-8 bg-maquis-olive" : "w-3 bg-sable"}`} />
          ))}
        </div>
      </section>

      {/* Description */}
      <section className="bg-ivoire" data-testid="property-description-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <p className="eyebrow">{t.property.description}</p>
            <p
              ref={descRef}
              data-testid="property-description"
              className={`mt-6 whitespace-pre-line font-serif text-xl sm:text-2xl leading-relaxed text-maquis-deep ${expanded ? "" : "line-clamp-[10]"}`}
            >
              {desc}
            </p>
            {(clamped || expanded) && (
              <button
                data-testid="description-toggle"
                onClick={() => setExpanded((e) => !e)}
                className="mt-6 inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-1 text-xs uppercase tracking-[0.2em] text-maquis-deep transition-colors duration-300 hover:border-maquis-deep"
              >
                {expanded ? t.property.show_less : t.property.show_more}
              </button>
            )}
          </Reveal>
        </div>
      </section>

      {/* Vidéo importée */}
      {videoFile && (
        <section className="mx-auto max-w-5xl px-5 sm:px-8 py-20 sm:py-28" data-testid="property-video-section">
          <Reveal>
            <p className="eyebrow">{t.property.video}</p>
            <video
              data-testid="property-video-player"
              src={videoFile}
              controls
              playsInline
              preload="metadata"
              className="mt-6 w-full rounded-md bg-mer"
            />
          </Reveal>
        </section>
      )}

      {/* Disponibilités */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28" data-testid="property-availability-section">
        <Reveal>
          <p className="eyebrow">{t.property.availability}</p>
          <div className="mt-8 flex flex-col items-start gap-10 lg:flex-row lg:items-center lg:gap-20">
            <div className="rounded-md border border-maquis-deep/10 bg-casse p-6" data-testid="availability-calendar">
              <DayPicker
                mode="range"
                selected={stayRange}
                onSelect={setStayRange}
                excludeDisabled
                numberOfMonths={isMobile ? 1 : 2}
                locale={lang === "fr" ? frLocale : undefined}
                disabled={[{ before: new Date() }, ...blocked]}
                modifiers={{ blocked }}
                modifiersStyles={{ blocked: { textDecoration: "line-through", color: "#B08D57" } }}
              />
            </div>
            <div className="w-full max-w-xs space-y-4 text-sm text-maquis-olive">
              <p className="flex items-center gap-3">
                <span className="inline-block h-3 w-3 rounded-full bg-maquis-olive" /> {t.property.available}
              </p>
              <p className="flex items-center gap-3">
                <span className="inline-block h-3 w-3 rounded-full bg-[#B08D57]" /> {t.property.unavailable}
              </p>
              {stayRange?.from && stayRange?.to && (
                <div className="rounded-md border border-maquis-deep/10 bg-casse p-5" data-testid="property-estimate">
                  <p className="eyebrow">{t.property.estimate_title}</p>
                  <label className="field-label mt-4">{t.property.guests}</label>
                  <select
                    data-testid="estimate-guests-select"
                    value={estGuests}
                    onChange={(e) => setEstGuests(Number(e.target.value))}
                    className="field-input mt-1"
                  >
                    {Array.from({ length: property.guests || 2 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  {estimate?.available && (
                    <div className="mt-4 space-y-2 border-t border-maquis-deep/10 pt-4">
                      <p className="flex justify-between gap-4">
                        <span className="text-maquis-soft">{t.property.estimate_base} · {estimate.nights} {t.property.estimate_nights}</span>
                        <span className="font-medium text-maquis-deep" data-testid="estimate-base">{estimate.base.toLocaleString("fr-FR")} €</span>
                      </p>
                      {estimate.extra > 0 && (
                        <p className="flex justify-between gap-4" data-testid="estimate-extra">
                          <span className="text-maquis-soft">{t.property.estimate_extra} (+{estimate.extra_guests})</span>
                          <span className="font-medium text-maquis-deep">{estimate.extra.toLocaleString("fr-FR")} €</span>
                        </p>
                      )}
                      <p className="flex items-baseline justify-between gap-4 pt-1">
                        <span className="text-maquis-deep">{t.property.estimate_total}</span>
                        <span className="font-serif text-xl text-maquis-deep" data-testid="estimate-total">{estimate.total.toLocaleString("fr-FR")} €</span>
                      </p>
                      <p className="text-xs italic leading-relaxed text-maquis-soft">{t.property.estimate_note}</p>
                    </div>
                  )}
                </div>
              )}
              <button
                data-testid="property-reserve-button"
                onClick={() => {
                  openBooking({
                    propertyId: property.id,
                    arrival: stayRange?.from ? isoDay(stayRange.from) : "",
                    departure: stayRange?.to ? isoDay(stayRange.to) : "",
                    guests: estGuests,
                  });
                }}
                className="btn-outline-elegant mt-6 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
              >
                {t.property.reserve}
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Avis */}
      {reviews.length > 0 && (
        <section className="bg-ivoire" data-testid="property-reviews-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
            <Reveal>
              <p className="eyebrow">{t.property.reviews}</p>
              <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {reviews.map((r) => (
                  <figure key={r.id} data-testid={`property-review-${r.id}`} className="rounded-md border border-maquis-deep/10 bg-casse p-6">
                    <div className="flex gap-1" data-testid={`review-stars-${r.id}`}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={14} strokeWidth={1.25} className={s <= r.rating ? "fill-[#B08D57] text-[#B08D57]" : "text-sable"} />
                      ))}
                    </div>
                    {r.message && (
                      <blockquote className="mt-4 text-sm leading-relaxed text-maquis-olive">« {r.message} »</blockquote>
                    )}
                    <figcaption className="mt-4 text-xs uppercase tracking-[0.15em] text-maquis-soft">
                      {[r.first_name, r.last_name].filter(Boolean).join(" ")}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-mer/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button className="absolute right-6 top-6 text-casse/80 transition-colors hover:text-casse" data-testid="lightbox-close" aria-label="Fermer">
            <X size={28} strokeWidth={1.25} />
          </button>
          <button
            data-testid="lightbox-prev"
            aria-label="Photo précédente"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + property.images.length) % property.images.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-casse/15 p-3 text-casse transition-colors hover:bg-casse/30"
          >
            <ChevronLeft size={26} strokeWidth={1.5} />
          </button>
          <img src={property.images[lightbox]} alt={property.name} onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full rounded-md object-contain" />
          <button
            data-testid="lightbox-next"
            aria-label="Photo suivante"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % property.images.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-casse/15 p-3 text-casse transition-colors hover:bg-casse/30"
          >
            <ChevronRight size={26} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </main>
  );
}
